import React from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, Divider, Box, IconButton, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import dayjs from 'dayjs';

const ProductionStatusDetails = ({ item, column, details, itemName, budget, threshold, onClose }) => {
    if (!item) return <Typography variant="h6">Select an item to view details</Typography>;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const handleFileClick = async (event) => {
        event.stopPropagation();
        const fileUrl = event.currentTarget.getAttribute('fileUrl');
        const fileName = event.currentTarget.getAttribute('fileName');

        if (fileUrl) {
            try {
                // File downloads are public (no token required)
                // Use direct fetch instead of api service
                const baseUrl = process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT
                    ? ''  // Relative URL for production
                    : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`;
                
                const response = await fetch(`${baseUrl}/expense/uploads/${fileUrl}`, {
                    method: 'GET',
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = fileName;
                    link.click();
                    // Clean up the object URL
                    setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 100);
                } else {
                    const errorData = await response.json().catch(() => ({ message: response.statusText }));
                    alert(`Error downloading file: ${errorData.message || response.statusText}`);
                }
            } catch (error) {
                console.error("Error fetching file:", error);
                alert(`Error fetching file: ${error.message || 'Unknown error'}`);
            }
        }
    };

    // Filter the relevant data based on `pci_id` (item) and `exp_d_episode` (column)
    const filteredData = details.flatMap((category) =>
        category.flatMap((itemData) => {
            if (itemData.exp_assigned_episodes.includes(column) && itemData.pci_id === item) {
                return {
                    exp_assigned_episodes: itemData.exp_assigned_episodes.length,
                    exp_pred: itemData.exp_pred,
                    exp_file_name: itemData.exp_original_filename || null,
                    exp_url: itemData.exp_file || null,
                    exp_beneficiary: itemData.expense_beneficiaries_descr.eb_company_name || null,
                    exp_inv_no: itemData.exp_inv_no || null,
                    exp_amount: itemData.exp_amount || null,
                    exp_date: itemData?.exp_date ? dayjs(itemData.exp_date).format('DD-MM-YYYY') : null,
                    exp_date_raw: itemData?.exp_date || null, // Keep raw date for sorting
                    expense_details: itemData.expense_details.filter((detail) => detail.exp_d_episode === column),
                };
            }
            return [];
        })
    ).sort((a, b) => {
        // First, sort by exp_pred (predictions first)
        if (a.exp_pred && !b.exp_pred) return -1;
        if (!a.exp_pred && b.exp_pred) return 1;
        
        // If both are predictions or both are not, sort by date ascending
        // Handle null dates by putting them at the end
        if (!a.exp_date_raw && !b.exp_date_raw) return 0;
        if (!a.exp_date_raw) return 1;
        if (!b.exp_date_raw) return -1;
        return new Date(a.exp_date_raw) - new Date(b.exp_date_raw);
    });

    const total_of_episode = filteredData.reduce((acc, data) => {
        return acc + data.expense_details.reduce((sum, detail) => sum + detail.exp_d_amount, 0);
    }, 0);
    const isExceeded = total_of_episode > budget;
    const isBelowThreshold = !isExceeded && (total_of_episode < budget * threshold);

    const tableData = filteredData.map((data) => ({
        group: [
            {
                field: data.exp_pred ? 'Αριθμός Τιμολογίου (Πρόβλεψη) ' : 'Αριθμός Τιμολογίου ',
                value: data.exp_inv_no || '-',
                rowStyle: { backgroundColor: '#d9d9d9', color: '#424242', borderTopLeftRadius: 4, borderTopRightRadius: 4 }
            },
            {
                field: 'Ημερομηνία Τιμολογίου ',
                value: data.exp_date || '-',
                rowStyle: { backgroundColor: '#ffffff', color: '#424242' }
            },
            {
                field: 'Επεισόδια ',
                value: data.exp_assigned_episodes,
                rowStyle: { backgroundColor: '#ffffff', color: '#424242', textAlign: 'right' }
            },
            {
                field: 'Σύνολο Τιμολογίου ',
                value: formatCurrency(data.exp_amount),
                rowStyle: { backgroundColor: '#ffffff', color: '#424242' }
            },
            ...data.expense_details.map((detail) => ({
                field: 'Κόστος Επεισοδίου',
                value: formatCurrency(detail.exp_d_amount),
                rowStyle: {  backgroundColor: '#ffffff', color: '#424242' },
            })),
            {

                field: 'Αρχείο',
                value: data.exp_url && (
                    <a
                        href="#"
                        onClick={handleFileClick}
                        fileUrl={data.exp_url}
                        fileName={data.exp_file_name}
                    >
                        <AttachFileIcon color="primary" />
                    </a>
                ),
                rowStyle: { backgroundColor: '#ffffff', color: '#424242' }

            },
            {
                field: 'Συναλασσόμενος ',
                value: data.exp_beneficiary || '-',
                rowStyle: { borderRadius: 2,backgroundColor: '#ffffff', color: '#424242', textAlign: 'right' }
            },
        ],
    }));

    return (
        <Paper elevation={3} 
        sx={{ 
            p: 2, 
            borderRadius: 2,
            backgroundColor: '#f4f6f8',     
            height: '100%',    
            overflow: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 transparent',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '3px',
            }, 
            }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, backgroundColor: '#e0e0e0', borderRadius: 1, p: 2 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#424242' }} align="center">
                        {itemName}
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#424242' }} align="center">
                        Επεισόδιο {column}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: '#424242' }}>
                    <CloseIcon />
                </IconButton>
            </Stack>

            <Divider sx={{ marginBottom: 2 }} />

            {/* Add a row to show itemOnEpisodeCost with conditional formatting */}
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 2, fontWeight: 'bold' }}>
                Συνολικό κόστος:
                <span style={{
                    color: isExceeded ? 'red' : isBelowThreshold ? 'orange' : 'inherit',
                    display: 'flex', // Ensure it appears inline next to the text
                    justifyContent: 'flex-start',
                    marginLeft: 10, // Space between the label and value
                }}>
                    {formatCurrency(total_of_episode)}
                    {isBelowThreshold && !isExceeded && (
                        <WarningIcon fontSize="small" style={{ justifyContent: 'flex-start', display: 'flex', marginRight: 'auto', color: 'orange' }} />
                    )}
                </span>
            </Typography>


            <Divider />

            <TableContainer component={Box}>
                <Table>
                    <TableBody>
                        {tableData.map((group, index) => (
                            <TableRow key={index} >
                                {/* Each group will be wrapped in a Box to ensure proper spacing and borders */}
                                <TableCell colSpan={2} sx={{ padding: 1, border: 'none' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            border: '1px solid #ccc',
                                            borderRadius: 2,
                                            mb: 2, // Margin between each group
                                        }}
                                    >
                                        {group.group.map((field, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: 1,
                                                    ...field.rowStyle
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{field.field}</Typography>
                                                <Typography variant="body2">{field.value}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default ProductionStatusDetails;
